import React, { useMemo, useRef, useEffect, useState } from "react";
import { Group, ActionIcon, Button, Tooltip, Popover } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Calendar,
} from "lucide-react";

type Props = {
  value: Date; // seçili tarih
  onChange: (d: Date) => void; // tarih değişimi
  minDate?: Date;
  maxDate?: Date;
  windowSize?: 1 | 3 | 5 | 7; // mobilde 3, masaüstünde 5/7 güzel duruyor
  locale?: string; // varsayılan "tr-TR"
  className?: string; // opsiyonel ekstra sınıf
};

const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

const addDays = (d: Date, n: number) => {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + n);
  return startOfDay(nd);
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const clamp = (d: Date, minDate?: Date, maxDate?: Date) => {
  const sd = startOfDay(d);
  if (minDate && sd < startOfDay(minDate)) return startOfDay(minDate);
  if (maxDate && sd > startOfDay(maxDate)) return startOfDay(maxDate);
  return sd;
};

export function DatePaginationBar({
  value,
  onChange,
  minDate,
  maxDate,
  windowSize = 5,
  locale = "tr-TR",
  className,
}: Props) {
  const today = startOfDay(new Date());
  const [opened, setOpened] = useState(false);

  // Aktif çipi görünür alana ortala
  const activeChipRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeChipRef.current?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [value]);

  // Mobilde dar ekranda otomatik 3'e düşür
  const effectiveWindowSize = useMemo<1 | 3 | 5 | 7>(() => {
    if (typeof window !== "undefined" && window.innerWidth < 460) return 3;
    return windowSize;
  }, [windowSize]);

  // Görünen "sayfa" çipleri (merkez + sağ/sol)
  const chips = useMemo(() => {
    const half = Math.floor(effectiveWindowSize / 2);
    let start = -half;
    let end = half;

    // min/max'a yakınken pencereyi kaydır
    while (minDate && addDays(value, start) < startOfDay(minDate)) {
      start++;
      end++;
    }
    while (maxDate && addDays(value, end) > startOfDay(maxDate)) {
      start--;
      end--;
    }

    const arr: Date[] = [];
    for (let i = start; i <= end; i++) {
      const d = addDays(value, i);
      if (minDate && d < startOfDay(minDate)) continue;
      if (maxDate && d > startOfDay(maxDate)) continue;
      arr.push(d);
    }
    return arr;
  }, [value, effectiveWindowSize, minDate, maxDate]);

  const prevDisabled = !!(minDate && addDays(value, -1) < startOfDay(minDate));
  const nextDisabled = !!(maxDate && addDays(value, +1) > startOfDay(maxDate));
  const prevWeekDisabled = !!(
    minDate && addDays(value, -7) < startOfDay(minDate)
  );
  const nextWeekDisabled = !!(
    maxDate && addDays(value, +7) > startOfDay(maxDate)
  );

  const go = (d: Date) => onChange(clamp(d, minDate, maxDate));

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") go(addDays(value, -1));
    if (e.key === "ArrowRight") go(addDays(value, +1));
    if (e.key === "PageUp") go(addDays(value, -7));
    if (e.key === "PageDown") go(addDays(value, +7));
    if (e.key.toLowerCase() === "t") go(today);
  };

  return (
    <div
      role="toolbar"
      aria-label="Tarih gezgini"
      tabIndex={0}
      onKeyDown={onKeyDown}
      className={[
        "fixed bottom-2 left-1/2 -translate-x-1/2 z-50",
        // mobil: neredeyse tam genişlik; masaüstü: rahat bir max genişlik
        "w-[calc(100vw-1rem)] max-w-[980px]",
        // yarı saydam kart + kenarlık
        "bg-white/90 dark:bg-zinc-800/90 backdrop-blur supports-[backdrop-filter]:bg-white/60",
        "rounded-2xl shadow-lg border border-zinc-200 dark:border-zinc-700",
        "px-2.5 py-2 md:px-3 md:py-2.5",
        className || "",
      ].join(" ")}
    >
      <Group justify="center" gap="xs" wrap="nowrap">
        {/* 7 gün geri */}
        <Tooltip label="7 gün geri">
          <ActionIcon
            variant="subtle"
            radius="xl"
            size="xl"
            onClick={() => go(addDays(value, -7))}
            disabled={prevWeekDisabled}
            aria-label="7 gün geri"
          >
            <ChevronsLeft className="w-5 h-5 md:w-6 md:h-6" strokeWidth={3} />
          </ActionIcon>
        </Tooltip>

        {/* 1 gün geri */}
        <Tooltip label="1 gün geri">
          <ActionIcon
            variant="subtle"
            radius="xl"
            size="xl"
            onClick={() => go(addDays(value, -1))}
            disabled={prevDisabled}
            aria-label="1 gün geri"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" strokeWidth={3.5} />
          </ActionIcon>
        </Tooltip>

        {/* Tarih çipleri */}
        <div className="flex gap-1 px-1 overflow-x-auto no-scrollbar max-w-full">
          {chips.map((d) => {
            const active = isSameDay(d, value);
            const key = d.toISOString().slice(0, 10);
            const weekday = d.toLocaleDateString(locale, { weekday: "short" }); // Pzt, Sal...
            const dayMonth = d.toLocaleDateString(locale, {
              day: "2-digit",
              month: "2-digit",
            });

            return (
              <button
                key={key}
                ref={active ? activeChipRef : undefined}
                onClick={() => go(d)}
                aria-pressed={active}
                aria-label={`${weekday} ${dayMonth}`}
                className={[
                  "flex flex-col items-center justify-center",
                  "rounded-2xl transition",
                  // Dokunma hedefi & tipografi: mobilde büyük, masaüstünde daha da büyük
                  "min-w-[68px] md:min-w-[84px] px-2.5 py-2 md:px-3 md:py-2.5",
                  active
                    ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                    : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600",
                ].join(" ")}
              >
                <span className="leading-none opacity-90">{weekday}</span>
                <span className="text-base md:text-lg font-semibold leading-none mt-0.5">
                  {dayMonth}
                </span>
              </button>
            );
          })}
        </div>

        {/* 1 gün ileri */}
        <Tooltip label="1 gün ileri">
          <ActionIcon
            variant="subtle"
            radius="xl"
            size="xl"
            onClick={() => go(addDays(value, +1))}
            disabled={nextDisabled}
            aria-label="1 gün ileri"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" strokeWidth={3.5} />
          </ActionIcon>
        </Tooltip>

        {/* 7 gün ileri */}
        <Tooltip label="7 gün ileri">
          <ActionIcon
            variant="subtle"
            radius="xl"
            size="xl"
            onClick={() => go(addDays(value, +7))}
            disabled={nextWeekDisabled}
            aria-label="7 gün ileri"
          >
            <ChevronsRight className="w-5 h-5 md:w-6 md:h-6" strokeWidth={3} />
          </ActionIcon>
        </Tooltip>

        {/* Bugün */}
        {/* <Button
          size="xs"
          variant="light"
          onClick={() => go(today)}
          disabled={isSameDay(value, today)}
          className="px-3 md:px-4 md:text-sm"
        >
          Bugün
        </Button> */}
      </Group>
    </div>
  );
}
